"use client";

import React, { useRef } from "react";

import { cn } from "@/lib/utils";
import Image from "next/image";
import { PortableTextBlock } from "sanity";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import CryptoPillar from "@/components/global/ui/CryptoPillar";
import { motion, useScroll, useTransform, useInView } from "framer-motion";

import values1 from "@/public/global/values/values1.jpg";
import values1Futures from "@/public/futures/values1.jpg";
import values2 from "@/public/global/values/values2.jpg";
import progress from "@/public/global/values/progress.svg";
import values3 from "@/public/global/values/values3.jpg";
import calendar from "@/public/global/values/calendar.svg";
import cursor from "@/public/global/values/cursor.svg";
import values4 from "@/public/global/values/values4.jpg";
import values4Futures from "@/public/futures/values4.png";
import wheel from "@/public/global/values/wheel.png";
import dollarIcon from "@/public/global/values/dollar-icon.svg";
import ethereumCoin from "@/public/global/values/ethereum-coin.png";
import bitcoinCoin from "@/public/global/values/bitcoin-coin.png";
import solanaCoin from "@/public/global/values/solana-coin.png";
import values5 from "@/public/global/values/values5.jpg";
import values5Futures from "@/public/futures/values5.png";
import values5bg from "@/public/global/values/value5-bg.png";
import atlas from "@/public/global/values/atlas.png";
import person1 from "@/public/global/values/person1.png";
import person2 from "@/public/global/values/person2.png";
import person3 from "@/public/global/values/person3.png";

const BentoContainer = ({
  children,
  className,
  delay = 0,
}: {
  children?: React.ReactNode;
  className?: string;
  delay?: number;
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40, filter: "blur(12px)" }}
      animate={
        isInView
          ? { opacity: 1, y: 0, filter: "blur(0px)" }
          : { opacity: 0, y: 40, filter: "blur(12px)" }
      }
      transition={{ duration: 0.7, ease: [0.25, 0.4, 0.25, 1], delay }}
      className={cn(
        "rounded-[2rem] lg:rounded-[2.5rem] aspect-[398/335] lg:aspect-auto w-[min(60rem,100%)] overflow-clip relative",
        className,
      )}
    >
      {children}
    </motion.div>
  );
};

export default function ValuesSection({
  title,
  value1,
  value2,
  value3,
  value4,
  value5,
  futures = false,
  className,
}: {
  title?: PortableTextBlock[];
  value1: PortableTextBlock[];
  value2: PortableTextBlock[];
  value3: PortableTextBlock[];
  value4: PortableTextBlock[];
  value5: PortableTextBlock[];
  futures?: boolean;
  className?: string;
}) {
  const parallaxRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: parallaxRef,
    offset: ["start end", "end start"],
  });

  const yEthereum = useTransform(scrollYProgress, [0, 1], ["-5%", "5%"]);
  const yBitcoin = useTransform(scrollYProgress, [0, 1], ["7%", "-7%"]);
  const ySolana = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section
      ref={parallaxRef}
      className={cn(
        "relative overflow-clip py-[12rem] lg:py-[22rem]",
        className,
      )}
    >
      <div className="container flex relative flex-col items-center">
        {title && (
          <motion.div
            initial={{ opacity: 0, y: 30, filter: "blur(10px)" }}
            whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6, ease: [0.25, 0.4, 0.25, 1] }}
            className="lg:w-auto richtext w-[32.5rem] mb-[3rem] sm:w-[40.5rem] lg:mb-[6rem]"
          >
            <PortableText value={title} components={richTextComponent} />
          </motion.div>
        )}

        <div className="w-[min(146.5rem,100%)] flex flex-col gap-[2rem] lg:gap-[1.5rem]">
          <div className="flex flex-col items-center lg:flex-row lg:items-stretch gap-[2rem] lg:gap-[1.5rem]">
            <BentoContainer className="p-[2.5rem] lg:w-[44.375rem] lg:min-h-[38.125rem]" delay={0}>
              <Image
                className="absolute left-0 top-0 !w-full !h-full object-cover"
                src={futures ? values1Futures : values1}
                quality={100}
                alt=""
              />

              {!futures && (
                <>
                  <CryptoPillar
                    className="left-[3rem] -bottom-[2rem] h-[22rem] opacity-30"
                    trailColors="bg-white backdrop-blur-[1rem]"
                    badgeColors="[&_img]:hidden"
                    type="ethereum"
                    style={{ y: yEthereum }}
                  />
                  <CryptoPillar
                    className="left-[10rem] -bottom-[2rem] h-[28.375rem] opacity-70"
                    trailColors="bg-white backdrop-blur-[1rem]"
                    badgeColors="[&_img]:hidden"
                    type="bitcoin"
                    style={{ y: yBitcoin }}
                  />
                  <CryptoPillar
                    className="left-[16.875rem] -bottom-[2rem] h-[13rem] opacity-40"
                    trailColors="bg-white backdrop-blur-[1rem]"
                    badgeColors="[&_img]:hidden"
                    type="solana"
                    style={{ y: ySolana }}
                  />
                </>
              )}

              {value1 && (
                <div className="richtext richtext-start prose-p:text-white/70 [&_.font-gestura]:text-white prose-p:text-[2rem] lg:prose-p:text-[2.25rem] prose-p:leading-[1.2] relative">
                  <PortableText
                    value={value1}
                    components={richTextComponent}
                  />
                </div>
              )}
            </BentoContainer>

            <BentoContainer className="lg:w-auto p-[2.5rem] lg:grow lg:min-h-[38.125rem]" delay={0.1}>
              <Image
                className="absolute left-0 top-0 !w-full !h-full object-cover"
                src={values2}
                quality={100}
                alt=""
              />

              <motion.div
                className="absolute left-[2.5rem] bottom-[6.625rem] rounded-[1rem] bg-[#EFF0F0]/[.35] backdrop-blur w-[min(26rem,80%)] aspect-[208/85]"
                style={{
                  x: useTransform(scrollYProgress, [0, 1], ["-2%", "2%"]),
                  y: useTransform(scrollYProgress, [0, 1], ["-1%", "1%"]),
                }}
              />
              <motion.div
                className="absolute left-[3.625rem] bottom-[5.625rem] rounded-[1rem] bg-[#F9F9F9]/[.35] backdrop-blur w-[min(30.625rem,80%)] aspect-[245/84]"
                style={{
                  x: useTransform(scrollYProgress, [0, 1], ["-4%", "4%"]),
                  y: useTransform(scrollYProgress, [0, 1], ["-2%", "2%"]),
                }}
              />
              <motion.div
                className="absolute left-[5rem] bottom-[2.5rem] rounded-[1rem] bg-white/[.78] backdrop-blur w-[min(34.125rem,90%)] p-[.75rem] lg:p-[1rem]"
                style={{
                  x: useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]),
                  y: useTransform(scrollYProgress, [0, 1], ["-3%", "3%"]),
                }}
              >
                <Image
                  className="!w-full"
                  src={progress}
                  quality={100}
                  alt=""
                />
              </motion.div>

              {value2 && (
                <div className="richtext richtext-start prose-p:text-white/70 [&_.font-gestura]:text-white prose-p:text-[2.25rem] prose-p:leading-[1.2] relative">
                  <PortableText value={value2} components={richTextComponent} />
                </div>
              )}
            </BentoContainer>

            <BentoContainer className="p-[2.5rem] lg:w-[44.375rem] lg:min-h-[38.125rem]" delay={0.2}>
              <Image
                className="absolute left-0 top-0 !w-full !h-full object-cover"
                src={values3}
                quality={100}
                alt=""
              />
              <Image
                className="absolute right-0 bottom-0 w-[calc(100%-3rem)]"
                src={calendar}
                alt="calendar"
              />
              <motion.div
                className="absolute right-[10.5rem] bottom-[5rem] w-[9rem]"
                style={{
                  x: useTransform(scrollYProgress, [0, 1], ["10%", "-10%"]),
                  y: useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]),
                }}
              >
                <Image className="!w-full" src={cursor} alt="cursor" />
              </motion.div>

              {value3 && (
                <div className="richtext richtext-start prose-p:text-white/70 [&_.font-gestura]:text-white prose-p:text-[2.25rem] prose-p:leading-[1.2] relative">
                  <PortableText value={value3} components={richTextComponent} />
                </div>
              )}
            </BentoContainer>
          </div>

          <div className="flex flex-col items-center lg:flex-row lg:items-stretch gap-[2rem] lg:gap-[1.5rem]">
            <BentoContainer className="p-[2.5rem] lg:w-[51.625rem] lg:min-h-[42.5rem]" delay={0}>
              <Image
                className="absolute left-0 top-0 !w-full !h-full object-cover"
                src={values4}
                quality={100}
                alt=""
              />
              <motion.div
                className="absolute left-1/2 -bottom-[22rem] !w-[55.125rem]"
                style={{
                  x: "calc(-50% - 0.5rem)",
                  rotate: useTransform(scrollYProgress, [0, 1], [-8, 8]),
                }}
              >
                <Image className="!w-full" src={wheel} alt="wheel" />
              </motion.div>

              <Image
                className="absolute left-1/2 -translate-x-[calc(50%+.5rem)] bottom-[29rem] !w-[7.5rem]"
                src={dollarIcon}
                alt="dollar icon"
              />

              {!futures ? (
                <>
                  <motion.div
                    className="absolute bottom-[4rem] left-[calc(50%-15rem)] w-[8.375rem]"
                    style={{
                      y: useTransform(scrollYProgress, [0, 1], ["3%", "-3%"]),
                    }}
                  >
                    <Image
                      className="!w-full drop-shadow-[0_24px_19px_rgba(0,0,0,.77)]"
                      src={ethereumCoin}
                      alt="ethereum coin"
                    />
                  </motion.div>

                  <motion.div
                    className="absolute bottom-[11rem] left-1/2 !-translate-x-[calc(50%+.5rem)] w-[9.375rem]"
                    style={{
                      y: useTransform(scrollYProgress, [0, 1], ["-15%", "15%"]),
                    }}
                  >
                    <Image
                      className="!w-full drop-shadow-[0_24px_19px_rgba(0,0,0,.77)]"
                      src={bitcoinCoin}
                      alt="bitcoin coin"
                    />
                  </motion.div>

                  <motion.div
                    className="absolute bottom-[4rem] left-[calc(50%+5rem)] w-[8.125rem]"
                    style={{
                      y: useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]),
                    }}
                  >
                    <Image
                      className="!w-full drop-shadow-[0_24px_19px_rgba(0,0,0,.77)]"
                      src={solanaCoin}
                      alt="solana coin"
                    />
                  </motion.div>
                </>
              ) : (
                <Image
                  className="absolute bottom-0 left-1/2 -translate-x-1/2 !w-[90%]"
                  src={values4Futures}
                  quality={100}
                  alt=""
                />
              )}

              {value4 && (
                <div className="richtext richtext-start prose-p:text-white/70 [&_.font-gestura]:text-white prose-p:text-[2.25rem] prose-p:leading-[1.2] relative">
                  <PortableText value={value4} components={richTextComponent} />
                </div>
              )}
            </BentoContainer>

            <BentoContainer className="lg:w-auto p-[2.5rem] lg:grow lg:min-h-[42.5rem]" delay={0.15}>
              <Image
                className="absolute left-0 top-0 !w-full !h-full object-cover"
                src={values5}
                quality={95}
                alt=""
              />
              <Image
                className="absolute left-1/2 -translate-x-1/2 -bottom-[3rem] lg:bottom-0 !w-[max(calc(100%-3rem),76rem)] !max-w-none"
                src={values5bg}
                quality={100}
                alt=""
              />
              <Image
                className={cn(
                  "absolute left-1/2 bottom-0",
                  futures
                    ? "!w-[40rem] lg:!w-[44rem] -translate-x-[55%]"
                    : "!w-[23rem] lg:!w-[48.25rem] -translate-x-1/2 z-10"
                )}
                src={futures ? values5Futures : atlas}
                quality={100}
                alt=""
              />

              <motion.div
                className="absolute top-[22rem] left-[1.5rem] w-[6rem] lg:top-[10.5rem] lg:left-[13rem] lg:w-[8.25rem]"
                style={{ y: yEthereum }}
              >
                <Image className="!w-full" src={person1} quality={100} alt="" />
              </motion.div>

              <motion.div
                className="absolute top-[11rem] right-[7.5rem] w-[6rem] lg:top-[2.5rem] lg:right-[27rem] lg:w-[8.25rem]"
                style={{ y: yBitcoin }}
              >
                <Image className="!w-full" src={person2} quality={100} alt="" />
              </motion.div>

              <motion.div
                className="absolute bottom-[4rem] right-[3.125rem] w-[6rem] lg:bottom-[5rem] lg:right-[4.5rem] lg:w-[8.25rem]"
                style={{ y: ySolana }}
              >
                <Image className="!w-full" src={person3} quality={100} alt="" />
              </motion.div>

              {value5 && (
                <div className="richtext richtext-start prose-p:text-black/70 [&_.font-gestura]:text-black prose-p:text-[2.25rem] prose-p:leading-[1.2] relative">
                  <PortableText value={value5} components={richTextComponent} />
                </div>
              )}
            </BentoContainer>
          </div>
        </div>
      </div>
    </section>
  );
}
